import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const SecurityReport = () => {
    const { url } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = `Rapport Sécurité - ${url}`;
        fetchReport();
    }, [url]);

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/get_security_report/${encodeURIComponent(url)}`);
            if (response.data.status === 'ok') {
                const data = response.data.data;
                // Parse details if it's a string
                if (typeof data.details === 'string') {
                    try {
                        data.parsedDetails = JSON.parse(data.details);
                    } catch (e) {
                        data.parsedDetails = {};
                    }
                } else {
                    data.parsedDetails = data.details || {};
                }
                // Safety-net dedup: a file reported in uploadsPhp should never
                // also appear in the generic malware list. Cleans up rows
                // written before the scanner's dedup fix.
                const pd = data.parsedDetails || {};
                if (Array.isArray(pd.uploadsPhp) && Array.isArray(pd.malware)) {
                    const upSet = new Set(pd.uploadsPhp);
                    pd.malware = pd.malware.filter(f => !upSet.has(f));
                }
                setReport(data);
            }
        } catch (err) {
            console.error('Error fetching security report', err);
            setError(err.response?.data?.message || 'Erreur lors du chargement du rapport');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Jamais';
        return new Date(dateStr).toLocaleString('fr-CH', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Build a lookup map { "<category>::<file_path>" → first_seen } from the
    // findings list returned by the API, so each file row can display when it
    // was first observed.
    const findingsMap = (() => {
        const map = {};
        const findings = report?.findings || report?.parsedDetails?.findings;
        if (Array.isArray(findings)) {
            for (const f of findings) {
                if (!f || !f.file_path || !f.category) continue;
                map[`${f.category}::${f.file_path}`] = f.first_seen;
            }
        }
        return map;
    })();

    const renderIssueList = (title, files, type) => {
        if (!files || files.length === 0) return null;

        const colors = {
            malware: { bg: '#fee2e2', text: '#991b1b', icon: 'fa-virus' },
            modified: { bg: '#fef3c7', text: '#92400e', icon: 'fa-file-signature' },
            added: { bg: '#dcfce7', text: '#166534', icon: 'fa-file-circle-plus' },
            deleted: { bg: '#f3f4f6', text: '#374151', icon: 'fa-file-circle-xmark' },
            uploadsPhp: { bg: '#fee2e2', text: '#991b1b', icon: 'fa-file-code' }
        };

        const theme = colors[type] || colors.deleted;

        return (
            <div className="mb-20">
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: theme.text }}>
                    <i className={`fa-solid ${theme.icon}`}></i>
                    {title} ({files.length})
                </h4>
                <div style={{ background: theme.bg, borderRadius: '8px', padding: '15px', border: `1px solid ${theme.text}22` }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '13px' }}>
                        {files.map((file, idx) => {
                            const firstSeen = findingsMap[`${type}::${file}`];
                            return (
                                <li key={idx} style={{
                                    padding: '6px 0',
                                    borderBottom: idx === files.length - 1 ? 'none' : `1px solid ${theme.text}11`,
                                    wordBreak: 'break-all',
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '10px'
                                }}>
                                    <span style={{ opacity: 0.6 }}>•</span>
                                    <code>{file}</code>
                                    {firstSeen && (
                                        <span
                                            title={`Première détection : ${formatDate(firstSeen)}`}
                                            style={{
                                                fontSize: '11px',
                                                color: theme.text,
                                                opacity: 0.75,
                                                background: '#fff',
                                                border: `1px solid ${theme.text}22`,
                                                borderRadius: '4px',
                                                padding: '2px 8px',
                                                fontWeight: 500,
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <i className="fa-regular fa-clock" style={{ marginRight: 5 }} />
                                            Détecté pour la première fois le {formatDate(firstSeen)}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        );
    };

    return (
        <div className="container mx-auto p-20">
            {/* Header Area */}
            < div className="display-flex justify-between align-items-center mb-20 wrap gap-20 justify-content-space-between" style={{ flexWrap: 'wrap' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '15px', margin: 0, fontSize: '28px', color: '#1a202c', marginBottom: '10' }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1dbab0 0%, #007adf 100%)',
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(29, 186, 176, 0.3)'
                    }}>
                        <i className="fa-solid fa-shield-halved fa-lg"></i>
                    </div>
                    Analyse de Sécurité
                </h2>
                <button
                    className="button-third"
                    onClick={() => window.close()}
                    style={{
                        borderRadius: '10px',
                        padding: '10px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        color: '#64748b',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <i className="fa-solid fa-xmark"></i>
                    Fermer le rapport
                </button>
            </div >

            {
                loading ? (
                    <div className="card bg-white p-60 text-center border-radius-10 shadow-lg" style={{ border: 'none' }} >
                        <i className="fa-solid fa-circle-notch fa-spin fa-3x mb-20" style={{ color: '#1dbab0' }}></i>
                        <h3 className="color-third">Initialisation de l'analyse...</h3>
                        <p style={{ color: '#94a3b8' }}>Nous récupérons les logs de scan les plus récents.</p>
                    </div >
                ) : error ? (
                    <div className="card bg-white p-60 text-center border-radius-10 shadow-lg" style={{ border: 'none' }}>
                        <div style={{ color: '#ef4444', marginBottom: '20px' }}>
                            <i className="fa-solid fa-triangle-exclamation fa-4x"></i>
                        </div>
                        <h3 style={{ color: '#ef4444', marginBottom: '10px' }}>Oups ! Une erreur est survenue</h3>
                        <p style={{ color: '#64748b', marginBottom: '30px' }}>{error}</p>
                        <button className="button-primary" style={{ padding: '12px 30px', borderRadius: '8px' }} onClick={fetchReport}>
                            Réessayer l'analyse
                        </button>
                    </div>
                ) : (
                    <div className="display-flex flex-direction-column gap-30">
                        {/* Main Info Card */}
                        <div className="card bg-white p-0 border-radius-15 shadow-lg overflow-hidden mb-20" style={{ border: 'none' }}>
                            <div style={{
                                background: report.status === 'critical'
                                    ? 'linear-gradient(90deg, #fee2e2 0%, #fff 100%)'
                                    : report.status === 'warning'
                                        ? 'linear-gradient(90deg, #fff7ed 0%, #fff 100%)'
                                        : 'linear-gradient(90deg, #dcfce7 0%, #fff 100%)',
                                padding: '25px 30px',
                                borderLeft: `6px solid ${report.status === 'critical' ? '#ef4444' : report.status === 'warning' ? '#f97316' : '#22c55e'}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '20px',
                                borderRadius: '10px 10px 0 0',
                            }}>
                                <div>
                                    <h3 style={{ margin: '0 0 5px 0', fontSize: '22px', wordBreak: 'break-all', color: '#1e293b', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                        <span>{url}</span>
                                        {report.hosting && (
                                            <span style={{
                                                fontSize: '13px',
                                                fontWeight: 500,
                                                color: '#475569',
                                                background: '#e2e8f0',
                                                padding: '3px 10px',
                                                borderRadius: '6px',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                <i className="fa-regular fa-server mr-5"></i>{report.hosting}
                                            </span>
                                        )}
                                    </h3>
                                    <div className="display-flex align-items-center gap-15" style={{ fontSize: '14px', color: '#64748b' }}>
                                        <span><i className="fa-regular fa-clock mr-5"></i> Scan exécuté le {formatDate(report.last_scan)}</span>
                                    </div>
                                </div>
                                <div style={{
                                    background: report.status === 'critical' ? '#ef4444' : report.status === 'warning' ? '#f97316' : '#22c55e',
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '12px',
                                    fontWeight: '800',
                                    fontSize: '14px',
                                    letterSpacing: '1px',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                                }}>
                                    {report.status === 'critical' ? 'CRITIQUE' : report.status === 'warning' ? 'MAINTENANCE' : 'SÉCURISÉ'}
                                </div>
                            </div>

                            <div style={{ padding: '30px' }}>
                                {report.status === 'critical' ? (
                                    <>
                                        <div className="mb-20" style={{ padding: '15px', background: '#fff9f9', borderRadius: '10px', border: '1px solid #fee2e2', marginBottom: '20px' }}>
                                            <p style={{ margin: 0, color: '#991b1b', fontSize: '15px', lineHeight: '1.6' }}>
                                                <strong>Alerte d'intégrité :</strong> Des anomalies ont été détectées lors de la comparaison avec les fichiers officiels de WordPress, ou de fichiers suspects. Veuillez examiner les fichiers listés ci-dessous pour prévenir toute intrusion ou corruption.
                                            </p>
                                        </div>

                                        <div className="display-flex flex-direction-column gap-10">
                                            {renderIssueList('Malware & Code Suspect', report.parsedDetails.malware, 'malware')}
                                            {renderIssueList('.php dans wp-content/uploads', report.parsedDetails.uploadsPhp, 'uploadsPhp')}
                                            {renderIssueList('Fichiers modifiés (Mismatches)', report.parsedDetails.modified, 'modified')}
                                            {renderIssueList('Fichiers ajoutés (différents d\'une clean install WP)', report.parsedDetails.added, 'added')}
                                            {renderIssueList('Fichiers supprimés (devraient être présents)', report.parsedDetails.deleted, 'deleted')}
                                        </div>
                                    </>
                                ) : report.status === 'warning' ? (
                                    <>
                                        <div className="mb-20" style={{ padding: '15px', background: '#fff7ed', borderRadius: '10px', border: '1px solid #fed7aa', marginBottom: '20px' }}>
                                            <p style={{ margin: 0, color: '#9a3412', fontSize: '15px', lineHeight: '1.6' }}>
                                                <strong>Mise à jour incomplète :</strong> Des fichiers du cœur WordPress sont manquants. Cela indique probablement une mise à jour automatique partielle. <strong>Ce n'est pas une intrusion</strong>, mais une mise à jour manuelle de WordPress est recommandée.
                                            </p>
                                        </div>
                                        <div className="display-flex flex-direction-column gap-10">
                                            {renderIssueList('Fichiers supprimés (devraient être présents)', report.parsedDetails.deleted, 'deleted')}
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                                        <div style={{
                                            width: '100px',
                                            height: '100px',
                                            background: '#dcfce7',
                                            color: '#22c55e',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 25px auto'
                                        }}>
                                            <i className="fa-solid fa-check fa-4x"></i>
                                        </div>
                                        <h3 style={{ color: '#1e293b', fontSize: '24px' }}>Tout est en ordre !</h3>
                                        <p style={{ color: '#64748b', maxWidth: '500px', margin: '0 auto', fontSize: '16px', lineHeight: '1.6' }}>
                                            Le scan n'a détecté aucune anomalie. Les fichiers du cœur de votre site correspondent exactement aux signatures officielles.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Information Grid */}
                        <div className="display-flex gap-20" style={{ flexWrap: 'wrap' }}>
                            {/* WP Version Logic Explanation */}
                            <div className="card bg-white p-20 border-radius-15 shadow-sm flex-1" style={{ border: 'none', minWidth: '300px' }}>
                                <h4 style={{ color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                    <i className="fa-solid fa-circle-info" style={{ color: '#007adf' }}></i>
                                    Version de WordPress
                                </h4>
                                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', margin: 0 }}>
                                    {report.parsedDetails.version ? (
                                        <>
                                            Le scanner a identifié <strong>WordPress {report.parsedDetails.version}</strong>.
                                            <br /><br />
                                            <span style={{ fontStyle: 'italic', fontSize: '13px' }}>Note : La version n'est calculée qu'en cas d'anomalies initiales pour effectuer une comparaison approfondie avec tous les fichiers officiels.</span>
                                        </>
                                    ) : (
                                        <>
                                            La version n'est pas affichée car le site a passé les tests de premier niveau avec succès.
                                            <br /><br />
                                            Le scanner n'active la détection de version et le scan MD5 profond que si des fichiers suspects ou des changements structurels sont détectés lors du scan rapide.
                                        </>
                                    )}
                                </p>
                            </div>

                            {/* Process Explanation */}
                            <div className="card bg-white p-20 border-radius-15 shadow-sm flex-1" style={{ border: 'none', minWidth: '300px', background: '#fcfcfc' }}>
                                <h4 style={{ color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                    <i className="fa-solid fa-microscope" style={{ color: '#1dbab0' }}></i>
                                    Fonctionnement du Scan
                                </h4>
                                <ul style={{ fontSize: '13px', color: '#64748b', paddingLeft: '20px', lineHeight: '1.8' }}>
                                    <li>Vérification des fichiers ajoutés (suspects/malware)</li>
                                    <li>Scan des motifs de code malveillant connus (regex)</li>
                                    <li>Comparaison des fichiers cœur via les sommes MD5 officielles</li>
                                    <li>Ignorance intelligente de fichiers système (ex: .htaccess légitimes)</li>
                                    <li><strong>Dossier non scanné : wp-content/</strong>(néanmoins, les fichiers connus comme étant des honeypots/suspects sont vérifiés même s'ils sont dedans)</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
        </div >
    );
};

export default SecurityReport;
